import { TrendingUp, TrendingDown } from 'lucide-react'

interface MarketTickerProps {
  marketData?: {
    gold: {
      symbol: string
      name: string
      price_eur: number | null
      change_24h_percent: number | null
      available: boolean
    }
    crypto: Array<{
      id: string
      symbol: string
      name: string
      image: string
      price_eur: number
      change_24h_percent: number
    }>
    forex: {
      eur_usd: {
        rate: number
        symbol: string
      }
    }
  }
  isLoading?: boolean
}

export default function MarketTicker({ marketData, isLoading }: MarketTickerProps) {
  if (isLoading || !marketData) {
    return (
      <div className="bg-gray-100 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 overflow-hidden h-9">
        <div className="py-2.5 px-4">
          <div className="flex items-center gap-6">
            <div className="h-3 w-24 bg-gray-300 dark:bg-slate-700 animate-pulse rounded" />
            <div className="h-3 w-24 bg-gray-300 dark:bg-slate-700 animate-pulse rounded" />
            <div className="h-3 w-24 bg-gray-300 dark:bg-slate-700 animate-pulse rounded" />
          </div>
        </div>
      </div>
    )
  }

  // Prepare ticker items
  const tickerItems: Array<{
    icon: string
    isImage?: boolean
    symbol: string
    price: string
    change: number
  }> = []

  // Add gold
  if (marketData.gold.available && marketData.gold.price_eur) {
    tickerItems.push({
      icon: '🥇',
      symbol: marketData.gold.symbol,
      price: `€${marketData.gold.price_eur.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`,
      change: marketData.gold.change_24h_percent || 0,
    })
  }

  // Add EUR/USD
  tickerItems.push({
    icon: '💱',
    symbol: 'EUR/USD',
    price: marketData.forex.eur_usd.rate.toFixed(4),
    change: 0, // Forex doesn't have 24h change in our data
  })

  // Add crypto
  marketData.crypto.forEach(coin => {
    tickerItems.push({
      icon: coin.image,
      isImage: true,
      symbol: coin.symbol.toUpperCase(),
      price: `€${coin.price_eur.toLocaleString('it-IT', { maximumFractionDigits: coin.price_eur < 1 ? 4 : 0 })}`,
      change: coin.change_24h_percent,
    })
  })

  // Duplicate items for seamless loop
  const duplicatedItems = [...tickerItems, ...tickerItems, ...tickerItems]

  return (
    <div className="bg-gray-100 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 overflow-hidden relative h-9">
      <div className="ticker-wrapper">
        <div className="ticker-content">
          {duplicatedItems.map((item, index) => (
            <div 
              key={index}
              className="ticker-item inline-flex items-center gap-1.5 px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs leading-relaxed"
            >
              {item.isImage ? (
                <img src={item.icon} alt={item.symbol} className="w-3.5 h-3.5 rounded-full" />
              ) : (
                <span className="text-xs">{item.icon}</span>
              )}
              <span className="font-medium">{item.symbol}</span>
              <span className="text-gray-500 dark:text-gray-400">{item.price}</span>
              {item.change !== 0 && (
                <span className={`flex items-center gap-0.5 ${
                  item.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {item.change >= 0 ? (
                    <TrendingUp className="w-2.5 h-2.5" />
                  ) : (
                    <TrendingDown className="w-2.5 h-2.5" />
                  )}
                  {Math.abs(item.change).toFixed(1)}%
                </span>
              )}
              <span className="text-gray-300 dark:text-slate-700 mx-1">•</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fade edges - theme aware */}
      <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-gray-100 dark:from-slate-900 to-transparent pointer-events-none z-10" />
      <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-gray-100 dark:from-slate-900 to-transparent pointer-events-none z-10" />
    </div>
  )
}
